import React, { createContext, useContext, useEffect, useState } from 'react';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return undefined;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.signUp({ email, password });
  };

  const signIn = async (email, password) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.signOut();
  };

  const signInWithProvider = async provider => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });
  };

  const signInWithGoogleIdToken = async (token, nonce) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.signInWithIdToken({
      provider: 'google',
      token,
      nonce,
    });
  };

  const signInWithAppleIdToken = async (token, nonce, appleUser) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    const result = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token,
      nonce,
    });

    if (result.error || !appleUser?.name) {
      return result;
    }

    const fullName = [appleUser.name.firstName, appleUser.name.middleName, appleUser.name.lastName]
      .filter(Boolean)
      .join(' ');

    if (!fullName) {
      return result;
    }

    await supabase.auth.updateUser({
      data: {
        full_name: fullName,
        given_name: appleUser.name.firstName,
        family_name: appleUser.name.lastName,
      },
    });

    return result;
  };

  const signInWithPhone = async phone => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.signInWithOtp({ phone });
  };

  const verifyPhoneOtp = async (phone, token) => {
    if (!supabase) {
      return { error: { message: 'Supabase is not configured.' } };
    }

    return supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    signInWithProvider,
    signInWithGoogleIdToken,
    signInWithAppleIdToken,
    signInWithPhone,
    verifyPhoneOtp,
    isAuthenticated: Boolean(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
